// src/components/InternalLinks/InternalLinks.tsx

import React from 'react';
import styled from 'styled-components';
import type { InternalLink } from '@/utils/internalLinks';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import type { Fonts } from '@/theme/themes';

type Props = {
  heading?: string;
  links: InternalLink[];
  headingFontFamily?: keyof Fonts;
  linkFontFamily?: keyof Fonts;
  columns?: number;
  align?: 'center' | 'right' | 'left';
};

/* ================= WRAP ================= */

const Wrap = styled.nav<{
  $align: 'center' | 'right' | 'left';
}>`
  margin: 8px 0 0;
  text-align: ${({ $align }) => $align};
  direction: ${({ $align }) => ($align === 'left' ? 'ltr' : 'rtl')};
  width: 100%;
`;

/* ================= HEADING ================= */

const Heading = styled.h3<{ $fontFamily?: keyof Fonts }>`
  margin: 0 0 8px;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.descriptionColor};

  font-weight: 800;
  font-family: ${({ theme, $fontFamily }) =>
    $fontFamily ? theme.fonts[$fontFamily] : theme.fonts.rubikExtraBold};

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    font-size: 0.85rem;
  }
`;

/* ================= LIST ================= */

const List = styled.ul<{
  $columns: number;
  $align: 'center' | 'right' | 'left';
}>`
  margin: 0;
  padding: 0;
  list-style: none;

  display: grid;
  width: ${({ $align, $columns }) =>
    $align === 'center'
      ? `min(100%, calc(${$columns} * 220px + ${Math.max($columns - 1, 0)} * 16px))`
      : 'fit-content'};
  max-width: 100%;
  margin-inline: ${({ $align }) => ($align === 'center' ? 'auto' : '0 auto')};

  grid-template-columns: ${({ $align, $columns }) =>
    $align === 'center'
      ? `repeat(${$columns}, minmax(0, 1fr))`
      : `repeat(${$columns}, max-content)`};
  justify-content: ${({ $align }) => ($align === 'center' ? 'center' : 'start')};

  column-gap: 16px;
  row-gap: 8px;

  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    width: 100%;
    margin-inline: 0;
    column-gap: 10px;
  }
`;

/* ================= ITEM ================= */

const Item = styled.li<{
  $fontFamily?: keyof Fonts;
  $align: 'center' | 'right' | 'left';
}>`
  min-width: 0;
  direction: ${({ $align }) => ($align === 'left' ? 'ltr' : 'rtl')};

  display: flex;

  /* דסקטופ → לפי align */
  justify-content: ${({ $align }) =>
    $align === 'center'
      ? 'center'
      : $align === 'left'
      ? 'flex-start'
      : 'flex-start'};

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: ${({ $align }) => $align};
    min-width: 0;
    width: fit-content;
    min-height: 44px;
    padding: 4px 8px;
    box-sizing: border-box;

    color: ${({ theme }) => theme.descriptionColor};
    text-decoration: none;
    font-size: 0.9rem;
    line-height: 1.25;

    font-family: ${({ theme, $fontFamily }) =>
      $fontFamily ? theme.fonts[$fontFamily] : theme.fonts.rubikBoldItalic};

    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    max-width: 24ch;

    &:hover {
      text-decoration: underline;
      text-underline-offset: 4px;
    }
  }

  /* ================= MOBILE ================= */
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    justify-content: ${({ $align }) =>
      $align === 'left' ? 'flex-start' : 'center'};

    a {
      text-align: ${({ $align }) => ($align === 'left' ? 'left' : 'center')};
      margin-inline: ${({ $align }) =>
        $align === 'left' ? '0 auto' : 'auto'};
      max-width: none; /* ⭐️ שלא יחתוך מוקדם */
    }
  }
`;

/* ================= COMPONENT ================= */

export const InternalLinksSection: React.FC<Props> = ({
  heading,
  links,
  headingFontFamily,
  linkFontFamily,
  columns = 2,
  align = 'center',
}) => {
  if (!links || links.length === 0) return null;

  return (
    <Wrap $align={align}>
      {heading && (
        <Heading $fontFamily={headingFontFamily}>
          {heading}
        </Heading>
      )}

      <List $columns={columns} $align={align}>
        {links.map((l) => (
          <Item
            key={l.href}
            $fontFamily={linkFontFamily}
            $align={align}
          >
            <a
              href={l.href}
              title={l.title ?? l.label}
              aria-label={l.title ?? l.label}
            >
              {l.label}
            </a>
          </Item>
        ))}
      </List>
    </Wrap>
  );
};
